import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { LngLatBounds } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useRef } from 'react';
import { Map, MapRef, ViewState } from 'react-map-gl';
import { useDebounce, useMeasure } from 'react-use';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import config from '../../../../config';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { CropTypes } from '../../../../services/entities/impl/blfApi/schemas/variety';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../../commonUI/cssClasses';
import LoadingSpinner from '../../../commonUI/LoadingSpinner';
import { GeocoderControl } from '../../../map/features/geocoderControl/GeocoderControl';
import '../../../map/features/geocoderControl/GeocoderControl.css';
import { MapZoomModeButton } from '../../../map/features/mapButtons/components/MapZoomModeButton';
import {
  MapZoomModeEnum,
  MAX_ZOOM,
  selectBlockButtonWasClicked,
  selectViewBounds,
  selectZoomMode,
  setBlockViewBounds,
} from '../../../map/mapSlice';
import { getBoundsFromBlocks } from '../../../map/utils/getBoundsFromBlocks';
import { selectUploadedBlockGeoJSON } from '../../onboardingSlice';
import { useGetCurrOnboardBlock } from '../hooks/useGetCurrOnboardBlock';
import CalculatedAcresDisplay from './CalculatedAcresDisplay';
import DownloadBlockBoundaryButton from './DownloadBlockBoundaryButton';
import useCheckLayersOnboarding from './hooks/useCheckLayersOnboarding';
import BlockOrientationLayer from './layers/BlockOrientationLayer';
import BlockOutlineOnboardingLayer from './layers/BlockOutlineOnboardingLayer';
import SurroundingBlocksLayer from './layers/SurroundingBlocksLayer';
import MapDrawControls from './MapDrawControls';
import OpenBlockBoundaryFormButton from './OpenBlockBoundaryFormButton';
import ShowSurroundingBlocksOnMapButton from './ShowSurroundingBlocksButton';
const mapboxToken = config.mapbox.apiKey;

interface Props {
  showDrawControls?: boolean;
  showBlockBoundaryPopupButton?: boolean;
  showAddressSearch?: boolean;
  onboardingTabIndex?: number;
  showCalculatedAcres?: boolean;
}

const MapOnboarding: React.FC<Props> = ({
  showDrawControls = false,
  showBlockBoundaryPopupButton = false,
  showAddressSearch = false,
  onboardingTabIndex,
  showCalculatedAcres = true,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const mapRef = useRef<MapRef | null>(null);
  const [sizeRef, { width, height }] = useMeasure();
  const blockButtonWasClicked = useAppSelector(selectBlockButtonWasClicked);
  const zoomMode = useAppSelector(selectZoomMode);
  const viewBounds = useAppSelector(selectViewBounds);
  const { language } = useTranslation();
  const uploadedBlockGeoJSON = useAppSelector(selectUploadedBlockGeoJSON);
  const { t } = useTranslation();

  const layerExistenceDict = useCheckLayersOnboarding(mapRef.current);

  const {
    currentData: currBlock,
    isLoading: isLoadingBlock,
    isFetching: isFetchingBlock,
  } = useGetCurrOnboardBlock();

  // List of blocks to show on the map
  const blocks = useMemo(() => {
    const blocks: Block[] = [];
    if (currBlock) {
      blocks.push(currBlock);
    }

    if (uploadedBlockGeoJSON) {
      // For uploaded block, create a dummy block with the uploaded geoJSON. This is for displaying the uploaded block on the map only and doesn't get saved to the database.
      const dummyBlock: Block = {
        id: 'uploadedBlock',
        name: 'Uploaded Block',
        ranchID: 'uploadedBlock',
        acres: 1,
        plantSpacing: 1,
        rowSpacing: 1,
        geoPolygon: uploadedBlockGeoJSON,
        geoOrientation: {
          type: 'FeatureCollection',
          features: [],
        },
        fromDate: new Date().toISOString(),
        varietyID: 'varietyID',
        variety: {
          id: 'varietyID',
          customerID: 'customerId',
          name: 'Ventura',
          cropType: CropTypes.Blueberries,
          stages: [],
        },
      };
      blocks.push(dummyBlock);
    }

    return blocks;
  }, [currBlock, uploadedBlockGeoJSON]);

  // update the block boundary viewBounds whenever blocks change
  const blockViewBounds = useMemo(() => {
    return getBoundsFromBlocks(blocks);
  }, [blocks, currBlock]);

  useEffect(() => {
    if (blockViewBounds) {
      dispatch(setBlockViewBounds(blockViewBounds));
    } else {
      dispatch(setBlockViewBounds(null));
      resetMapView();
    }
  }, [blockViewBounds, currBlock]);

  // Center the map on the bounds of the currently selected blocks
  const centerMapOnBounds = (bounds: LngLatBounds) => {
    if (mapRef.current) {
      mapRef.current.fitBounds(bounds, {
        padding: 20,
      });
    }
  };

  const zoomMapWithFallback = (fallbackZoomMode: MapZoomModeEnum) => {
    if (viewBounds[zoomMode]) {
      centerMapOnBounds(viewBounds[zoomMode] as LngLatBounds);
    } else if (viewBounds[fallbackZoomMode]) {
      centerMapOnBounds(viewBounds[MapZoomModeEnum.blockLevel] as LngLatBounds);
    }
  };

  // Center the map when the list of blocks changes, a block button was clicked, or width/height changes
  useEffect(() => {
    if (zoomMode !== MapZoomModeEnum.manual) {
      zoomMapWithFallback(MapZoomModeEnum.blockLevel);
    }
  }, [viewBounds, zoomMode, blockButtonWasClicked]);

  // If width or height changes, force mapbox to resize (sometimes it wouldn't resize when navbar was open/closed)
  useDebounce(
    () => {
      if (
        zoomMode !== MapZoomModeEnum.manual &&
        mapRef.current &&
        width &&
        height
      ) {
        mapRef.current.resize();
        setTimeout(() => {
          // Skip if blockViewBounds is not set yet.  This is to prevent a bug where a new block is selected which changed the size of the form, which in turn changed the size of the map and triggered a map resize event which then caused the map to zoom back to the previous block's bounds instead of resetting to the world view.
          if (blockViewBounds) {
            zoomMapWithFallback(MapZoomModeEnum.blockLevel);
          }
        }, 100);
      }
    },
    100,
    [mapRef, width, height, blockViewBounds]
  );

  const resetMapView = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [-98.5795, 39.8283], // longitude and latitude for the default view
        zoom: 1, // zoom level for the world view
      });
    }
  };

  // Set initial view state as zoomed out world view
  let initialViewState: Partial<ViewState> & {
    bounds?: mapboxgl.LngLatBoundsLike | undefined;
    fitBoundsOptions?: mapboxgl.FitBoundsOptions | undefined;
  } = {
    longitude: -98.5795, // center of USA
    latitude: 39.8283,
    zoom: 1, // 1 is world view
  };

  // If there are blocks to show, set initial view state to bounds of blocks
  if (viewBounds[zoomMode] || viewBounds[MapZoomModeEnum.blockLevel]) {
    initialViewState = {
      bounds:
        viewBounds[zoomMode] ||
        viewBounds[MapZoomModeEnum.blockLevel] ||
        undefined,
    };
  }

  return (
    <Box
      ref={sizeRef}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: '10rem',
        height: '100%',
        minHeight: '16rem',
        ...flexCenter,
        position: 'relative',
      }}
    >
      <Map
        id='map'
        key={'map'}
        ref={mapRef}
        mapboxAccessToken={`${mapboxToken}`}
        reuseMaps
        trackResize
        initialViewState={initialViewState}
        mapStyle='mapbox://styles/mapbox/satellite-streets-v12'
        maxZoom={MAX_ZOOM - 1} // Point vanish if zoom level in point layer is higher than max zoom level in map so we -1
        style={{
          width: '100%',
          height: '100%',
          borderBottomLeftRadius: theme.borderRadius.primary,
          borderBottomRightRadius: theme.borderRadius.primary,
        }}
      >
        {showDrawControls && (
          <MapDrawControls onboardingTabIndex={onboardingTabIndex} />
        )}
        <SurroundingBlocksLayer
          mapRef={mapRef.current}
          layerExistenceDict={layerExistenceDict}
        />
        <BlockOutlineOnboardingLayer
          mapRef={mapRef.current}
          layerExistenceDict={layerExistenceDict}
          blocks={blocks}
        />
        <BlockOrientationLayer
          layerExistenceDict={layerExistenceDict}
          mapRef={mapRef.current}
        />
        {showAddressSearch && (
          <GeocoderControl
            mapboxAccessToken={`${mapboxToken}`}
            position='top-right'
            language={language}
            placeholder={t('Find address or coordinates')}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: showDrawControls ? 48 : 10,
            ...flexCenter,
            flexDirection: 'row',
            gap: '0.4rem',
          }}
        >
          <ShowSurroundingBlocksOnMapButton />
          <DownloadBlockBoundaryButton />
          <MapZoomModeButton />
        </Box>
        {showBlockBoundaryPopupButton && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
            }}
          >
            <OpenBlockBoundaryFormButton />
          </Box>
        )}
        {showCalculatedAcres && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 5,
              left: 5,
              zIndex: 5,
            }}
          >
            <CalculatedAcresDisplay />
          </Box>
        )}
        {(isLoadingBlock || isFetchingBlock) && (
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              ...flexCenter,
            }}
          >
            <Box
              sx={{
                ...flexCenter,
                bgcolor: '#1e2125b0',
                borderRadius: '50%',
                padding: '1rem',
              }}
            >
              <LoadingSpinner size='50px' />
            </Box>
          </Box>
        )}
      </Map>
    </Box>
  );
};

export default MapOnboarding;
